import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Slide, ToastContainer, toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { guardianTypesManagement } from "../../../../../../hooks/api/GuardianTypesManagement/GuardianTypesManagement";
import { DefaultLayout } from "../../../../../../layouts/Default";
import { TopNavigation } from "../../../../../../themes/Default/components/TopNavigation";
import { Button, ButtonBack } from "../../../../../../themes/Default/components/TopNavigation/components/Button";
import { Tabs } from "../../../../../../themes/Default/components/Tabs";
import { Main } from "../../../../../../themes/Default/components/Main";
import { Title } from "../../../../../../themes/Default/components/Title";
import { Breadcrumps } from "../../../../../../themes/Default/components/Breadcrumps";
import { Link } from "../../../../../../themes/Default/components/Breadcrumps/components/Link";
import { List } from "../../../../../../themes/Default/components/List";
import { Body as ListBody } from "../../../../../../themes/Default/components/List/components/Body";
import { Header as ListHeader } from "../../../../../../themes/Default/components/List/components/Header";
import { TableHead } from "../../../../../../themes/Default/components/List/components/Body/components/TableHead";
import { TableRow } from "../../../../../../themes/Default/components/List/components/Body/components/TableHead/components/TableRow";
import { TableHeadCell } from "../../../../../../themes/Default/components/List/components/Body/components/TableHead/components/TableRow/components/TableHeadCell";
import { TableBody } from "../../../../../../themes/Default/components/List/components/Body/components/TableBody";
import { TableDataCell } from "../../../../../../themes/Default/components/List/components/Body/components/TableBody/components/TableDataCell";
import { Checkbox } from "../../../../../../themes/Default/components/Form/components/Checkbox";
import { Pagination } from "../../../../../../themes/Default/components/List/components/Footer/components/Pagination";
import { Toaster } from "../../../../../../themes/Default/components/Toaster";

const GuardianTypes = () => {
    const { t } = useTranslation("common");
    const recordsPerPage = 10;
    const defaultFilters = {
        startPage: 0,
        limitPage: recordsPerPage,
    };
    const navigate = useNavigate();
    const [filters, setFilters] = useState(defaultFilters);
    const [totalRecords, setTotalRecords] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [disableDelete, setDisableDelete] = useState(true);
    const [selectedGuardianType, setSelectedGuardianType] = useState([]);
    const [guardianTypes, setGuardianTypes] = useState();
    const { useGuardianTypesListMutation, useDeleteGuardianTypesMutation } = guardianTypesManagement;
    const [guardianTypeList, { isLoading }] = useGuardianTypesListMutation();
    const [deleteGuardianType] = useDeleteGuardianTypesMutation();

    const tabs = [
        { title: t("noteTypes.title"), onClick: () => gotoNoteType() },
        { title: t("guardianTypes.title"), isActive: true },
        { title: t("municipalities.title"), onClick: () => gotoMunicipalities() },
    ];

    const gotoNoteType = () => {
        navigate("/settings/default/note-types");
    };

    const gotoMunicipalities = () => {
        navigate("/settings/default/municipalities");
    };

    const gotoNew = () => {
        navigate("/settings/default/guardian-types/add");
    };

    const onPageChange = (currentPage, startPage, limitPage) => {
        setCurrentPage(currentPage);
        setFilters({
            ...filters,
            startPage: startPage,
            limitPage: limitPage,
        });
    };

    const onSelectionChange = (id) => {
        setSelectedGuardianType((prevSelected) => {
            if (prevSelected.includes(id)) {
                return prevSelected.filter((recordId) => recordId !== id);
            } else {
                return [...prevSelected, id];
            }
        });
    };

    const onDelete = () => {
        toast.warn(
            ({ closeToast }) => (
                <Toaster
                    closeToast={closeToast}
                    description={t("areYouSure")}
                    optionNo={t("no")}
                    optionYes={t("yes")}
                    onClick={() => {
                        closeToast();
                        for (let i = 0; i < selectedGuardianType?.length; i++) {
                            const guardianTypeId = selectedGuardianType[i];
                            deleteGuardianType(guardianTypeId).then((result) => {
                                if (result?.error) {
                                    toast.error(result.error.data.error.responseMessage);
                                } else {
                                    guardianTypeList(filters).then((result) => {
                                        const guardianTypes = result?.data?.records;
                                        setGuardianTypes(guardianTypes);
                                        setTotalRecords(result?.data?.totalRecords);
                                        setDisableDelete(true);
                                        onSelectionChange(guardianTypeId);
                                    });
                                    toast.info(result?.data?.info?.responseMessage);
                                }
                            });
                        }
                    }}
                />
            ),
            {
                closeButton: false,
                autoClose: false,
                progress: false,
            },
        );
    };

    useEffect(() => {
        if (selectedGuardianType.length > 0) {
            setDisableDelete(false);
        } else {
            setDisableDelete(true);
        }
    }, [selectedGuardianType]);

    useEffect(() => {
        guardianTypeList(filters).then((result) => {
            const guardianTypes = result?.data?.records;
            setGuardianTypes(guardianTypes);
            setTotalRecords(result?.data?.totalRecords);
        });
    }, [filters, guardianTypeList]);

    return (
        <DefaultLayout>
            
            <TopNavigation>
                <ButtonBack to="/settings" />
                <Button title={t("guardianTypes.addNew")} icon="fa-regular fa-plus" onClick={gotoNew}></Button>
                <Button title={t("delete")} icon="fa-regular fa-trash-can" isDisabled={disableDelete} onClick={onDelete}></Button>
            </TopNavigation>
            <Tabs tabs={tabs} />
            <Main>
                <Title>{`${t("guardianTypes.title")} (${totalRecords})`}</Title>
                <Breadcrumps>
                    <Link title={t("settings.title")} icon="fa-gear" />
                    <Link title={t("settings.default")} />
                    <Link title={t("guardianTypes.title")} />
                    <Link title={t("settings.list")} isActive={true} />
                </Breadcrumps>
                {!isLoading && (
                    <>
                        <List>
                            <ListHeader></ListHeader>
                            <ListBody>
                                <TableHead>
                                    <TableRow>
                                        <TableHeadCell></TableHeadCell>
                                        <TableHeadCell>
                                            <span>{t("guardianTypes.name")}</span>
                                        </TableHeadCell>
                                        <TableHeadCell></TableHeadCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {guardianTypes?.length > 0 &&
                                        guardianTypes?.map((guardianType, index) => {
                                            return (
                                                <TableRow key={guardianType.id}>
                                                    <TableDataCell>
                                                        <Checkbox
                                                            name="isBlockClass"
                                                            value={guardianType.id}
                                                            onChange={() => onSelectionChange(guardianType.id)}
                                                        />
                                                    </TableDataCell>
                                                    <TableDataCell>{guardianType.name}</TableDataCell>
                                                    <TableDataCell></TableDataCell>
                                                </TableRow>
                                            );
                                        })}
                                </TableBody>
                            </ListBody>
                        </List>
                        <Pagination currentPage={currentPage} recordsPerPage={recordsPerPage} totalRecords={totalRecords} onClick={onPageChange} />
                    </>
                )}
            </Main>
        </DefaultLayout>
    );
};

export default GuardianTypes;
